export const GLYCEMIA_UNITS = {
    MG_DL: {label: 'mg/dL', min: 1, max: 600},
    MMOL_L: {label: 'mmol/L', min: 0.1, max: 33.3}
};

export const DEFAULT_GLYCEMIA_UNIT = 'MG_DL';

export const FIELDS = {
    patient: {
        name: {
            id: 'patient.name',
            isValid: (data) => data.patient.name?.length <= 100,
            isEmpty: (data) => data.patient.name?.length < 1
        },
        genre: {
            id: 'patient.genre',
            isValid: () => true,
            isEmpty: (data) => !data.patient.genre
        },
        age: {
            id: 'patient.age',
            isValid: (data) => data.patient.age <= 130,
            isEmpty: (data) => !data.patient.age
        },
        fiscalCode: {
            id: 'patient.fiscalCode',
            isValid: (data) => data.patient.fiscalCode?.length <= 16,
            isEmpty: (data) => !data.patient.fiscalCode?.trim()
        }
    },
    caregiverContact: {
        name: {
            id: 'caregiverContact.name',
            isValid: (data) => data.caregiverContact.name?.length <= 100,
            isEmpty: (data) => data.caregiverContact.name?.length < 1
        },
        type: {
            id: 'caregiverContact.type',
            isValid: () => true,
            isEmpty: (data) => !data.caregiverContact.type
        },
        phoneNumber: {
            id: 'caregiverContact.phoneNumber',
            isValid: (data) => data.caregiverContact.phoneNumber?.length <= 20,
            isEmpty: (data) => data.caregiverContact.phoneNumber?.length < 1
        }
    },
    anamnesis: {
        symptomsOnsetTimeKnown: {
            id: 'anamnesis.symptomsOnsetTimeKnown',
            isValid: (data) => data.anamnesis.symptomsOnsetTimeKnown !== null
        },
        symptomsOnsetTime: {
          id: 'anamnesis.symptomsOnsetTime',
          isValid: (data) => data.anamnesis.symptomsOnsetTime !== null
                          || data.anamnesis.symptomsOnsetTimeKnown === false
        },
        wakeUpStroke: {
            id: 'anamnesis.wakeUpStroke',
            isValid: (data) => data.anamnesis.wakeUpStroke !== null
                            || data.anamnesis.symptomsOnsetTimeKnown === true
        },
        bedTime: {
            id: 'anamnesis.bedTime',
            isValid: (data) => data.anamnesis.bedTime !== null
                            || data.anamnesis.wakeUpStroke === null
                            || data.anamnesis.wakeUpStroke === false
        },
        discoveryOrLastKnownWellTime: {
            id: 'anamnesis.discoveryOrLastKnownWellTime',
            isValid: (data) => data.anamnesis.discoveryOrLastKnownWellTime !== null
                            || data.anamnesis.wakeUpStroke === true
                            || data.anamnesis.wakeUpStroke === null
        },
        tookAnxiolytics48h: {
            id: 'anamnesis.tookAnxiolytics48h',
            isValid: (data) => data.anamnesis.tookAnxiolytics48h !== null
        },
        isEpileptic: {
            id: 'anamnesis.isEpileptic',
            isValid: (data) => data.anamnesis.isEpileptic !== null
        },
        tookAnticoagulants48h: {
            id: 'anamnesis.tookAnticoagulants48h',
            isValid: (data) => data.anamnesis.tookAnticoagulants48h !== null
        }
    },
    parameters: {
        bloodPressureMin: {
            id: 'parameters.bloodPressureMin',
            isValid: (data) => data.parameters.bloodPressureMin >= 1 && data.parameters.bloodPressureMin <= 500
        },
        bloodPressureMax: {
            id: 'parameters.bloodPressureMax',
            isValid: (data) => data.parameters.bloodPressureMax >= 1 && data.parameters.bloodPressureMax <= 500
        },
        oxygenSaturation: {
            id: 'parameters.oxygenSaturation',
            isValid: (data) => data.parameters.oxygenSaturation >= 1 && data.parameters.oxygenSaturation <= 100
        },
        glycemia: {
            id: 'parameters.glycemia',
            isValid: (data) => {
                const unit = GLYCEMIA_UNITS[data.parameters.glycemiaUnit ?? DEFAULT_GLYCEMIA_UNIT];
                return data.parameters.glycemia >= unit.min && data.parameters.glycemia <= unit.max;
            }
        }
    },
};

export const STEP_STATES = {
    COMPLETED: 'COMPLETED',
    INCOMPLETE_REQUIRED: 'INCOMPLETE_REQUIRED',
    INCOMPLETE_OPTIONAL: 'INCOMPLETE_OPTIONAL',
};

export const STEPS = [
    {
        id: 'ACCEPTANCE',
        label: "Accettazione",
        required: false,
        // Completato con il solo codice fiscale (vedi anche PreHospitalService#buildSteps)
        fields: [
            FIELDS.patient.fiscalCode,
        ],
    }, {
        id: 'ANAMNESIS',
        label: "Anamnesi",
        required: true,
        fields: [
            FIELDS.anamnesis.symptomsOnsetTimeKnown, FIELDS.anamnesis.discoveryOrLastKnownWellTime,
            FIELDS.anamnesis.symptomsOnsetTime, FIELDS.anamnesis.bedTime,
            FIELDS.anamnesis.tookAnticoagulants48h, FIELDS.anamnesis.wakeUpStroke,
            FIELDS.anamnesis.tookAnxiolytics48h, FIELDS.anamnesis.isEpileptic,
        ],
    }, {
        id: 'PARAMETERS',
        label: "Parametri vitali",
        required: true,
        fields: [
            FIELDS.parameters.bloodPressureMin, FIELDS.parameters.bloodPressureMax,
            FIELDS.parameters.oxygenSaturation, FIELDS.parameters.glycemia,
        ],
    }, {
        id: 'SCALES',
        label: "Scale mediche",
        required: true,
        fields: null
    }, {
        id: 'RESULTS',
        label: "Risultati",
        required: false,
        fields: null
    }
];

export const STEP_INDEX = Object.fromEntries(STEPS.map((step, index) => [step.id, index]));

export const STRUCTURE_TYPE_LABELS = {
    STROKE_CENTER: "Stroke Center",
    STROKE_UNIT: "Stroke Unit"
};

export const STRUCTURE_TYPE_DETAILS = {
    STROKE_CENTER: {label: "Stroke Center (IVT + EVT)", role: "HUB"},
    STROKE_UNIT: {label: "Stroke Unit (solo IVT)", role: "SPOKE"}
};
